import { Form } from "antd";
import { useState, useEffect } from "react";
import {
  deleteLanguage,
  updateLanguage,
  addLanguage,
} from "../repository/data.repository";
import { Language } from "../repository/data.default";
import DynamicForm from "../controls/DynamicForm";
import { LanguagesForm } from "./LanguagesView.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { getProblemMessage } from "../utils/http";

export interface LanguageFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: Language;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function LanguageFormDialog(props: LanguageFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<LanguagesForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (props.open) {
      form.setFieldsValue({ ...props.initialValues });
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function onError(error: unknown): void {
    setLoading(false);
    setErrorMessage(getProblemMessage(error, "Something went wrong, try again"));
  }

  function onSubmit(): void {
    const { name, code, alpha3Code, isTestLanguage } = values;
    setLoading(true);
    setErrorMessage(null);
    if (props.mode === "add") {
      addLanguage({ name, code, alpha3Code, isTestLanguage })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "delete") {
      deleteLanguage(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "update") {
      updateLanguage({
        uid: props.initialValues?.uid,
        rowVersion: props.initialValues?.rowVersion,
        name,
        code,
        alpha3Code,
        isTestLanguage,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else {
      setLoading(false);
      props.onFinish?.();
    }
  }

  function reset(): void {
    setValid(false);
    setErrorMessage(null);
    setLoading(false);
    form.resetFields();
  }

  const schemaControls = [
    {
      type: "textbox" as const,
      name: "name",
      label: "Name",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      type: "textbox" as const,
      name: "code",
      label: "Code (e.g. en-US)",
      rules: [{ required: true, message: "Code is required" }],
    },
    {
      type: "textbox" as const,
      name: "alpha3Code",
      label: "Alpha-3 Code (e.g. eng)",
    },
    {
      type: "togglebox" as const,
      name: "isTestLanguage",
      label: "Available for assessments",
      checkedText: "Yes",
      unCheckedText: "No",
    },
  ];

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Language", props.initialValues?.name)}
      onCancel={props.onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={props.onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {(props.mode === "add" || props.mode === "update") && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{ groups: [{ controls: schemaControls }] }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="language"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default LanguageFormDialog;
